// config/rbac.js
//
// Espejo en el frontend de `config/rbac.js` del API de talleres.
//
// Ojo con lo que esto ES y lo que NO es: acá se decide qué se DIBUJA (menú, botones,
// guardas de ruta). Quién puede hacer qué lo decide el servidor, que verifica el mismo
// mapa en cada petición. Esconder un botón no es una barrera de seguridad; la barrera
// son los `authorizeModulo` del backend.
//
// El mapa de permisos llega en `GET /sesion/permisos` y vive en el contexto de Auth.

export const ACCIONES = ['ver', 'editar', 'requiereAprobacion'];

/**
 * Las claves de módulo. Las etiquetas y descripciones las sirve el backend
 * (`GET /admin/roles/modulos`) para que la matriz no las duplique; acá solo hacen
 * falta las claves, que son con las que el menú y las rutas se etiquetan.
 */
export const MODULOS = [
  'dashboard',
  'usuarios',
  'roles',
  'recintos',
  'talleres',
  'matriculas',
  'ventas',
  'sala-espera',
  'torniquete',
  'casos',
  'descuentos',
  'agenda',
  'reporteria',
  'comunicaciones',
];

/**
 * ¿Este usuario puede `accion` sobre `modulo`?
 *
 * El administrador siempre puede: es la misma salida de emergencia que tiene el
 * backend, y sin ella un admin al que se le quitó un permiso por error no podría
 * entrar a la pantalla que se lo devuelve.
 */
export const can = (user, modulo, accion = 'ver') => {
  if (!user) return false;
  if (user.isAdmin === true || user.role === 'admin') return true;
  const permiso = user.permisos?.[modulo];
  if (!permiso) return false;
  return permiso[accion] === true;
};

/** ¿Los cambios de este usuario en este módulo pasan por la cola de revisión? */
export const requiereAprobacion = (user, modulo) => {
  if (!user) return false;
  if (user.isAdmin === true || user.role === 'admin') return false;
  return user.permisos?.[modulo]?.requiereAprobacion === true;
};

/**
 * ¿Puede aprobar lo que otros dejaron en la cola de este módulo?
 *
 * Editar, menos quien necesita que le aprueben: nadie se aprueba a sí mismo. Es la
 * condición con la que el backend protege `/taller/cambio/:id/aprobar`.
 */
export const puedeAprobar = (user, modulo) =>
  can(user, modulo, 'editar') && !requiereAprobacion(user, modulo);

/** ¿Este usuario ve alguna pantalla del panel? */
export const entraAlPanel = user => MODULOS.some(modulo => can(user, modulo, 'ver'));

/**
 * Reglas de coherencia de la matriz, iguales a las del servidor.
 *
 *  · Marcar «editar» marca «ver»: editar algo que no se puede ver no significa nada.
 *  · Desmarcar «ver» desmarca las otras dos.
 *  · «Requiere aprobación» solo se habilita con «editar»: es un matiz de CÓMO se
 *    edita, no un permiso aparte.
 *
 * Con esto un rol imposible no se puede ni dibujar, y el 422 del servidor queda de
 * red de seguridad en vez de ser la primera noticia.
 */
export const aplicarReglas = (permisos, modulo, accion, valor) => {
  const previo = permisos[modulo] || { ver: false, editar: false, requiereAprobacion: false };
  const siguiente = { ...previo, [accion]: valor };

  if (accion === 'ver' && !valor) {
    siguiente.editar = false;
    siguiente.requiereAprobacion = false;
  }
  if (accion === 'editar') {
    if (valor) siguiente.ver = true;
    else siguiente.requiereAprobacion = false;
  }

  return { ...permisos, [modulo]: siguiente };
};

/** Un mapa completo con todos los módulos en false. */
export const permisosVacios = () =>
  MODULOS.reduce((mapa, key) => {
    mapa[key] = { ver: false, editar: false, requiereAprobacion: false };
    return mapa;
  }, {});
